/**
 * Monetisation: how a venue pays to be listed.
 *
 * Two plans a vendor chooses between — COMMISSION (a cut of each booking, no
 * monthly fee) and SUBSCRIPTION (a fixed fee per cycle, no cut) — plus
 * FOUNDING_PARTNER, a fixed-term grant we hand out that the vendor cannot
 * choose or leave. Every amount the API returns is in paise, so nothing here is
 * a rupee figure; use `paiseToInr` at the edge.
 */

/** The two plans a vendor can actually choose between. */
export type SelectablePlan = 'COMMISSION' | 'SUBSCRIPTION';

/**
 * FOUNDING_PARTNER is granted by us, never chosen: it runs zero-commission for
 * a fixed term and then reverts on its own to `underlyingPlan`. It can come
 * back on any venue record, so every plan read has to allow for it.
 */
export type MonetisationPlan = SelectablePlan | 'FOUNDING_PARTNER';
export type BillingCycle = 'monthly' | 'yearly';

export type PlanStatus =
  | 'ACTIVE'
  | 'PENDING_PAYMENT'
  | 'PAST_DUE'
  | 'CANCELLED'
  | 'TRIALING';

export type MandateStatus = 'NONE' | 'PENDING' | 'ACTIVE' | 'CANCELLED' | 'FAILED';

/**
 * Why the vendor can (or cannot) change plan right now. The string union is
 * open-ended on purpose — the server adds reasons (FOUNDING_PARTNER_ACTIVE was
 * one) and an unknown reason must degrade to "show the server's message", never
 * to a type error or a silently unlocked button.
 */
export type SwitchReason =
  | 'NOT_ON_SUBSCRIPTION'
  | 'NO_ACTIVE_PERIOD'
  | 'WINDOW_ALWAYS_OPEN'
  | 'WINDOW_OPEN'
  | 'WINDOW_NOT_OPEN'
  | 'FOUNDING_PARTNER_ACTIVE'
  | (string & {});

export interface CyclePricing {
  billingCycle: BillingCycle;
  durationInMonths: number;
  basePricePaise: number;
  discountPaise: number;
  discountPercent: number;
  discountLabel: string | null;
  netPricePaise: number;
  gstPercent: number;
  gstPaise: number;
  payablePaise: number;
  comparedToMonthly?: {
    equivalentMonthlyPaise: number;
    savingPaise: number;
    savingPercent: number;
  };
}

export interface OnboardingPricing {
  label: string;
  listingFeePaise: number;
  gstApplicable: boolean;
  gstPercent: number;
  gstPaise: number;
  /** Zero unless a DBL_LIFETIME plan row exists; already inside payablePaise. */
  directBookingLinkPaise: number;
  directBookingLinkPlanId?: string;
  payablePaise: number;
}

export interface MonetisationPlans {
  availablePlans: MonetisationPlan[];
  gstPercent: number;
  onboarding: OnboardingPricing;
  commission: {
    plan: 'COMMISSION';
    commissionPercent: number;
    monthlyFeePaise: number;
    description: string;
  };
  subscription: {
    plan: 'SUBSCRIPTION';
    subscriptionCommissionPercent: number;
    description: string;
    cycles: CyclePricing[];
  };
}

export interface SwitchEligibility {
  canSwitch: boolean;
  reason: SwitchReason;
  alwaysOpen?: boolean;
  windowDays?: number;
  opensAt?: string;
  daysUntilOpen?: number;
  currentPeriodEnd?: string;
  effectiveAt?: string;
  /** Set while a grant holds the venue on one plan (founding partner). */
  plan?: MonetisationPlan;
  endsAt?: string;
  daysRemaining?: number;
  /** Vendor-facing sentence. Prefer it over anything composed here. */
  message?: string;
}

/** The founding-partner grant: zero commission until `endsAt`, then `revertsTo`. */
export interface FoundingPartner {
  plan: 'FOUNDING_PARTNER';
  isActive: boolean;
  startsAt: string;
  endsAt: string;
  daysRemaining: number;
  revertsTo: SelectablePlan;
}

export interface PendingPlanChange {
  fromPlan: MonetisationPlan;
  toPlan: SelectablePlan;
  effective: 'IMMEDIATE' | 'AT_PERIOD_END';
  effectiveAt: string | null;
  requestedAt: string;
  requestedBy: string;
  reason: string | null;
  mandateCancelled: boolean;
}

export interface Mandate {
  status: MandateStatus;
  provider: 'MANUAL' | 'RAZORPAY';
  mandateId?: string;
  maxAmountPaise?: number;
  authorisedAt?: string | null;
  cancelledAt: string | null;
  cancelReason?: string | null;
  lastFailureAt?: string | null;
  failureCount?: number;
}

export interface VenueMonetisation {
  venueId: string;
  plan: MonetisationPlan;
  /** What the venue falls back to when a grant ends. Only sent with a grant. */
  underlyingPlan?: SelectablePlan;
  foundingPartner?: FoundingPartner | null;
  /**
   * The server's flat answer to "may this venue change plan at all". False
   * during a founding-partner term; absent on older responses, so treat only an
   * explicit `false` as a lock.
   */
  canChangePlan?: boolean;
  status: PlanStatus;
  billingCycle: BillingCycle | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  payablePaise: number;
  mandate: Mandate;
  pendingPlanChange: PendingPlanChange | null;
  switchEligibility: SwitchEligibility;
}

export interface SubscribeResponse {
  subscriptionId: string;
  razorpayStatus: string;
  pricing: CyclePricing;
  firstDebitPaise: number;
  recurringPaise: number;
  /** Hand straight to `new Razorpay(...)` — carries subscription_id, not order_id. */
  checkout: Record<string, any>;
  /** Present when an abandoned checkout was picked back up. */
  resumed?: boolean;
}

export interface SwitchPlanResponse {
  outcome: 'PAYMENT_REQUIRED' | 'SCHEDULED' | 'SWITCHED';
  plan: SelectablePlan;
  pendingPlanChange?: PendingPlanChange | null;
  pricing?: CyclePricing;
  mandate?: Mandate;
  currentPeriodEnd?: string;
  effectiveAt?: string | null;
  message?: string;
}

/**
 * A venue whose plan is frozen by us, so nothing on screen may offer a switch.
 * `canChangePlan === false` is the server's own gate; the reason check covers
 * responses that predate it.
 */
export const isPlanChangeLocked = (venue?: VenueMonetisation | null) =>
  venue?.canChangePlan === false ||
  venue?.switchEligibility?.reason === 'FOUNDING_PARTNER_ACTIVE' ||
  Boolean(venue?.foundingPartner?.isActive);

/** Founding partners pay no commission either, so they read as zero commission. */
/**
 * A move onto the subscription whose payment was started and never finished —
 * the checkout was closed, or the mandate is still waiting on the UPI app.
 *
 * The server records it as a pending plan change, but nothing is booked in: the
 * vendor still has to pay. It is offered as "pay and switch" again, and
 * POST /switch-plan picks the same authorisation back up rather than starting
 * a second one.
 */
export const isAwaitingPayment = (venue?: VenueMonetisation | null) =>
  Boolean(
    venue &&
      venue.plan !== 'SUBSCRIPTION' &&
      venue.pendingPlanChange?.toPlan === 'SUBSCRIPTION' &&
      (venue.status === 'PENDING_PAYMENT' || venue.mandate?.status === 'PENDING'),
  );

export const isZeroCommission = (plan?: MonetisationPlan | null) =>
  plan === 'SUBSCRIPTION' || plan === 'FOUNDING_PARTNER';

export const planLabel = (plan?: MonetisationPlan | null) =>
  plan === 'FOUNDING_PARTNER'
    ? 'Founding Partner'
    : plan === 'SUBSCRIPTION'
      ? 'Zero commission'
      : 'Pay per booking';

/** Paise are the API's unit everywhere; rupees only exist for display. */
export const paiseToRupees = (paise: number | null | undefined) => (paise ?? 0) / 100;

export const formatPaise = (paise: number | null | undefined) => {
  const rupees = paiseToRupees(paise);
  return `₹${rupees.toLocaleString('en-IN', {
    minimumFractionDigits: Number.isInteger(rupees) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};
